import { CanonicalEncoder, concatBytes, utf8 } from "./encoding";
import {
  addressHash,
  controlCommitment,
  denomHash,
  parseField,
  randomField,
  reserveCommitment,
} from "./field";
import type { V2KeyContext, V2Keys } from "./keys";

export type ReserveNote = {
  version: 2;
  kind: "reserve";
  ownerKey: string;
  rho: string;
  denom: string;
  denomHash: string;
  amount: string;
  blinding: string;
  commitment: string;
};

export type ControlNote = {
  version: 2;
  kind: "control";
  ownerKey: string;
  rho: string;
  subwallet: string | null;
  subwalletHash: string;
  controlNonce: string;
  blinding: string;
  commitment: string;
};

export type V2Note = ReserveNote | ControlNote;

const MAX_U128 = (1n << 128n) - 1n;
const ZERO_FIELD = "0".repeat(64);

export const createReserveNote = async (
  keys: V2Keys,
  denom: string,
  amount: bigint,
  options: { allowZero?: boolean } = {}
): Promise<ReserveNote> => {
  if (
    !denom ||
    amount < 0n ||
    (!options.allowZero && amount === 0n) ||
    amount > MAX_U128
  ) {
    throw new Error("Invalid reserve note value");
  }
  const rho = randomField();
  const blinding = randomField();
  const hashedDenom = await denomHash(denom);
  const commitment = await reserveCommitment({
    ownerKey: keys.ownerKey,
    rho,
    denomHash: hashedDenom,
    amount,
    blinding,
  });
  return {
    version: 2,
    kind: "reserve",
    ownerKey: keys.ownerKey,
    rho,
    denom,
    denomHash: hashedDenom,
    amount: amount.toString(),
    blinding,
    commitment,
  };
};

export const createControlNote = async (
  keys: V2Keys,
  value: { subwallet: string | null; controlNonce: bigint }
): Promise<ControlNote> => {
  if (value.controlNonce < 0n || value.controlNonce > (1n << 64n) - 1n) {
    throw new Error("Control nonce is out of range");
  }
  const rho = randomField();
  const blinding = randomField();
  const subwalletHash = value.subwallet
    ? await addressHash(value.subwallet)
    : ZERO_FIELD;
  const commitment = await controlCommitment({
    ownerKey: keys.ownerKey,
    rho,
    subwalletHash,
    controlNonce: value.controlNonce,
    blinding,
  });
  return {
    version: 2,
    kind: "control",
    ownerKey: keys.ownerKey,
    rho,
    subwallet: value.subwallet,
    subwalletHash,
    controlNonce: value.controlNonce.toString(),
    blinding,
    commitment,
  };
};

const noteAad = (
  context: V2KeyContext,
  note: Pick<V2Note, "kind" | "commitment">
) =>
  new CanonicalEncoder()
    .pushBytes(utf8("redacted-v2-encrypted-note"))
    .pushString(context.chainId)
    .pushString(context.proxyAddress)
    .pushString(note.kind)
    .pushField(note.commitment)
    .finish();

const importScanKey = async (scanKey: Uint8Array, usages: KeyUsage[]) => {
  // WebCrypto import is asynchronous. Own a defensive copy before yielding so
  // locking/wallet switching can wipe the live session without changing an
  // in-flight encryption key to 0^32.
  const owned = scanKey.slice();
  try {
    return await crypto.subtle.importKey(
      "raw",
      owned,
      { name: "AES-GCM" },
      false,
      usages
    );
  } finally {
    owned.fill(0);
  }
};

export const encryptNote = async (
  keys: V2Keys,
  context: V2KeyContext,
  note: V2Note
): Promise<Uint8Array> => {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const key = await importScanKey(keys.scanKey, ["encrypt"]);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, additionalData: noteAad(context, note) },
      key,
      utf8(JSON.stringify(note))
    )
  );
  return concatBytes(Uint8Array.of(2), nonce, ciphertext);
};

const isCanonicalNote = (value: unknown): value is V2Note => {
  if (!value || typeof value !== "object") return false;
  const note = value as Record<string, unknown>;
  if (
    note.version !== 2 ||
    (note.kind !== "reserve" && note.kind !== "control") ||
    typeof note.ownerKey !== "string" ||
    typeof note.rho !== "string" ||
    typeof note.blinding !== "string" ||
    typeof note.commitment !== "string"
  ) {
    return false;
  }
  try {
    parseField(note.ownerKey);
    parseField(note.rho);
    parseField(note.blinding);
    parseField(note.commitment);
  } catch {
    return false;
  }
  return note.kind === "reserve"
    ? typeof note.denom === "string" &&
        typeof note.denomHash === "string" &&
        typeof note.amount === "string"
    : (typeof note.subwallet === "string" || note.subwallet === null) &&
        typeof note.subwalletHash === "string" &&
        typeof note.controlNonce === "string";
};

export const verifyNoteCommitment = async (note: V2Note): Promise<boolean> => {
  try {
    if (note.kind === "reserve") {
      const amount = BigInt(note.amount);
      return (
        note.denomHash === (await denomHash(note.denom)) &&
        note.commitment ===
          (await reserveCommitment({
            ownerKey: note.ownerKey,
            rho: note.rho,
            denomHash: note.denomHash,
            amount,
            blinding: note.blinding,
          }))
      );
    }
    const expectedSubwalletHash = note.subwallet
      ? await addressHash(note.subwallet)
      : ZERO_FIELD;
    return (
      expectedSubwalletHash === note.subwalletHash &&
      note.commitment ===
        (await controlCommitment({
          ownerKey: note.ownerKey,
          rho: note.rho,
          subwalletHash: note.subwalletHash,
          controlNonce: BigInt(note.controlNonce),
          blinding: note.blinding,
        }))
    );
  } catch {
    return false;
  }
};

export const decryptNote = async (params: {
  keys: V2Keys;
  context: V2KeyContext;
  kind: V2Note["kind"];
  commitment: string;
  encrypted: Uint8Array;
}): Promise<V2Note | null> => {
  if (params.encrypted.length < 30 || params.encrypted[0] !== 2) return null;
  try {
    const nonce = params.encrypted.slice(1, 13);
    const key = await importScanKey(params.keys.scanKey, ["decrypt"]);
    const plaintext = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: nonce,
          additionalData: noteAad(params.context, {
            kind: params.kind,
            commitment: params.commitment,
          }),
        },
        key,
        params.encrypted.slice(13)
      )
    );
    const value: unknown = JSON.parse(new TextDecoder().decode(plaintext));
    if (
      !isCanonicalNote(value) ||
      value.kind !== params.kind ||
      value.commitment !== params.commitment ||
      value.ownerKey !== params.keys.ownerKey ||
      !(await verifyNoteCommitment(value))
    ) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
};
